<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export type ViewerModelOption = {
  id: string
  title: string
  fileName: string
}

const props = defineProps<{
  options: ViewerModelOption[]
  activeId: string | null
  automatic: boolean
  loading?: boolean
  attach?: Element
}>()

const emit = defineEmits<{
  select: [bopUploadId: string | null]
}>()

const { t } = useI18n()

const activeOption = computed(() => props.options.find((o) => o.id === props.activeId) ?? null)
</script>

<template>
  <v-menu location="top end" :attach="attach" :offset="8">
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        size="small"
        variant="text"
        color="grey-darken-3"
        prepend-icon="view_in_ar"
        append-icon="arrow_drop_up"
        :loading="loading"
        :title="t('component.viewerModelPicker.title')"
        class="model-picker__activator"
      >
        <span class="model-picker__name">
          {{ activeOption?.title ?? t('component.viewerModelPicker.none') }}
        </span>
        <span v-if="automatic && activeOption" class="model-picker__mode">
          {{ t('component.viewerModelPicker.automatic') }}
        </span>
      </v-btn>
    </template>

    <v-list density="compact" min-width="260" max-width="360" class="model-picker__list">
      <v-list-subheader>{{ t('component.viewerModelPicker.menuTitle') }}</v-list-subheader>
      <v-list-item
        v-for="option in options"
        :key="option.id"
        :active="option.id === activeId"
        color="primary"
        :title="option.title"
        :subtitle="option.fileName"
        @click="emit('select', option.id)"
      >
        <template #append>
          <v-icon v-if="option.id === activeId" size="18">check</v-icon>
        </template>
      </v-list-item>
      <template v-if="!automatic">
        <v-divider class="my-1" />
        <v-list-item
          class="model-picker__automatic"
          prepend-icon="auto_mode"
          :title="t('component.viewerModelPicker.useAutomatic')"
          :subtitle="t('component.viewerModelPicker.useAutomaticHint')"
          @click="emit('select', null)"
        />
      </template>
    </v-list>
  </v-menu>
</template>

<style scoped>
.model-picker__activator {
  border-radius: 10px;
  text-transform: none;
  letter-spacing: normal;
  max-width: 280px;
}

.model-picker__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.model-picker__automatic :deep(.v-list-item__spacer) {
  width: 12px;
}

.model-picker__mode {
  margin-left: 6px;
  font-size: 0.85em;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}
</style>
